import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { useProject, useUpdateProject, useDeleteProject } from '@/hooks/useProjects';
import { useOrganization } from '@/hooks/useOrganization';
import { useEnvironments } from '@/hooks/useEnvironments';
import { useSecrets } from '@/hooks/useSecrets';
import { Card, CardContent } from '@/components/ui/card';
import { HomeIcon, BuildingIcon } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { useToast } from '@/hooks/useToast';
import { ResourcePageHeader } from '@/components/layout/ResourcePageHeader';
import { Loader } from '@/components/ui/loader';
import { ProjectInformationCard, SecurityAccessCard, StatisticsCard, DangerZoneCard } from './-components/settings';

export const Route = createFileRoute('/_authenticated/org/$orgId/projects/$id/settings')({
  component: ProjectSettingsPage,
});

function ProjectSettingsPage() {
  const { id: projectId, orgId } = Route.useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { organization } = useOrganization(orgId);
  const { project, isLoading: projectLoading } = useProject(projectId, organization?.id);
  const environmentsQuery = useEnvironments(projectId);
  const secretsQuery = useSecrets(projectId);
  const updateProjectMutation = useUpdateProject();
  const deleteProjectMutation = useDeleteProject();
  const toast = useToast();

  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: project?.name || '',
    description: project?.description || '',
  });

  // Update form data when project changes
  useEffect(() => {
    if (project) {
      setFormData({
        name: project.name || '',
        description: project.description || '',
      });
    }
  }, [project]);

  const handleSave = async () => {
    try {
      await updateProjectMutation.mutateAsync({
        projectId,
        organizationId: orgId,
        name: formData.name,
        description: formData.description,
      });
      toast.success('Project settings saved successfully');
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving project settings:', error);
      toast.error('Failed to save project settings');
    }
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this project? This action cannot be undone.')) {
      return;
    }
    
    try {
      await deleteProjectMutation.mutateAsync({
        projectId,
        organizationId: orgId,
      });
      toast.success('Project deleted successfully');
      // Navigate back to projects list
      navigate({ to: `/org/${orgId}` });
    } catch (error) {
      console.error('Error deleting project:', error);
      toast.error('Failed to delete project');
    }
  };

  const isLoading = projectLoading || environmentsQuery.isLoading || secretsQuery.isLoading;
  
  const environments = environmentsQuery.data?.environments || [];
  const secrets = secretsQuery.data?.secrets || [];

  if (isLoading) {
    return (
      <div className="space-y-8">
        <ResourcePageHeader
          breadcrumbs={[
            { name: 'Dashboard', to: '/dashboard', icon: HomeIcon },
            { name: organization?.name || 'Organization', to: `/org/${orgId}`, icon: BuildingIcon },
            { name: 'Project', to: `/org/${orgId}/projects/${projectId}` },
            { name: t('navigation.settings'), to: `/org/${orgId}/projects/${projectId}/settings` },
          ]}
          title={t('navigation.settings')}
          backTo={`/org/${orgId}/projects/${projectId}`}
          variant="project"
        />
        <div className="flex justify-center py-16">
          <Loader variant="spinner" size="lg" color="accent" text={t('common.loading')} />
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="space-y-8">
        <ResourcePageHeader
          breadcrumbs={[
            { name: 'Dashboard', to: '/dashboard', icon: HomeIcon },
            { name: organization?.name || 'Organization', to: `/org/${orgId}`, icon: BuildingIcon },
            { name: 'Project', to: `/org/${orgId}/projects/${projectId}` },
            { name: t('navigation.settings'), to: `/org/${orgId}/projects/${projectId}/settings` },
          ]}
          title={t('navigation.settings')}
          backTo={`/org/${orgId}/projects/${projectId}`}
          variant="project"
        />
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900">Project not found</h2>
              <p className="text-gray-600 mt-2">The project you're looking for doesn't exist.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const breadcrumbs = [
    {
      name: 'Dashboard',
      to: '/dashboard',
      icon: HomeIcon,
    },
    {
      name: organization?.name || 'Organization',
      to: `/org/${organization?.slug || orgId}`,
      icon: BuildingIcon,
    },
    {
      name: project.name,
      to: `/org/${orgId}/projects/${projectId}`,
    },
    {
      name: t('navigation.settings'),
      to: `/org/${orgId}/projects/${projectId}/settings`,
    },
  ];

  return (
    <div className="space-y-8">
      <ResourcePageHeader
        breadcrumbs={breadcrumbs}
        title={`${project.name} - ${t('navigation.settings')}`}
        subtitle="Manage project settings and configuration"
        backTo={`/org/${orgId}/projects/${projectId}`}
        variant="project"
      />

      {/* Settings Content */}
      <div className="space-y-6">
        <ProjectInformationCard
          projectId={projectId}
          projectName={project.name}
          projectDescription={project.description}
          createdAt={project.createdAt}
          formData={formData}
          isEditing={isEditing}
          onEdit={() => setIsEditing(true)}
          onSave={handleSave}
          onCancel={() => {
            setIsEditing(false);
            setFormData({
              name: project.name,
              description: project.description || '',
            });
          }}
          onFormDataChange={(data) => setFormData(data)}
        />

        <SecurityAccessCard />

        <StatisticsCard
          environmentCount={environments.length}
          secretCount={secrets.length}
          lastUpdatedAt={project.updatedAt}
        />

        <DangerZoneCard onDelete={handleDelete} />
      </div>
    </div>
  );
}
